import { SubscriptionNode, SubscriptionNodeTypes } from '../ConsumerModels/SubcriptionNode';

export const egressIdToName: Record<number, string> = {
  1: 'API',
  2: 'ServiceBus',
  3: 'DataLake',
  4: 'Kusto',
  5: 'GraphQLAPI',
  6: 'GraphQLAPI',
  7: 'Snapshot',
};

/*
 * Add attributes from a previous request (drafts)
 */
export const addAttributesFromDraft = (
  formattedSubscriptionNodes: SubscriptionNode[],
  draftedSubscriptionNodes: SubscriptionNode[]
): SubscriptionNode[] => {

  const newSubscriptionNodes: SubscriptionNode[] = JSON.parse(JSON.stringify(formattedSubscriptionNodes)); // deep copy

  // Format Attribute level with isChecked field
  draftedSubscriptionNodes.forEach((tenant: SubscriptionNode) => {
    const tenantIndex = findNodeIndexById(tenant.id, newSubscriptionNodes);
    if (tenantIndex == -1) {
      return; // Not found
    }
    tenant.children.forEach((cdm: SubscriptionNode) => {
      const cdmIndex = findNodeIndexById(cdm.id, newSubscriptionNodes[tenantIndex].children);
      if (cdmIndex == -1) {
        return; // Not found
      }
      cdm.children.forEach((attribute: SubscriptionNode) => {
        const attributeIndex = findNodeIndexByName(
          attribute.name,
          newSubscriptionNodes[tenantIndex].children[cdmIndex].children
        );
        if (attributeIndex == -1) {
          return; // Not found
        }
        newSubscriptionNodes[tenantIndex].children[cdmIndex].children[attributeIndex].isCheckedAttribute = true;
      });
    });
  });

  // Format CDM level with isAllSelected field
  newSubscriptionNodes.forEach((tenant: SubscriptionNode) => {
    tenant.children.forEach((cdm: SubscriptionNode) => {
      const numberOfChildren = cdm.children.length;
      let numberOfChecked = 0;
      cdm.children.forEach((attribute: SubscriptionNode) => {
        if (attribute.isCheckedAttribute) {
          numberOfChecked = numberOfChecked + 1;
        }
      });
      if (numberOfChecked === numberOfChildren) {
        cdm.isAllSelected = true;
      } else {
        cdm.isAllSelected = false;
      }
    });
  });

  return newSubscriptionNodes;
};

/*
 * Finds the number of leaves in a node tree (for draft requests)
 */
export const findLeavesOfNodeList = (nodeList: SubscriptionNode[]): number => {
  if (!nodeList || nodeList.length == 0) {
    return 1;
  }
  let total = 0;
  nodeList.forEach((childNode) => {
    const numberOfChild: number = findLeavesOfNodeList(childNode.children);
    total = total + numberOfChild;
  });
  return total;
};

/*
 * Formats different fields to each level of the SubscriptionNode tree
 * - Tenant Level: isDropdownExpand
 * - Cdm Level: isAllSelected
 * - Attribute Level: isChecked
 */
export const addSubscriptionNodeFields = (tenants: SubscriptionNode[]): SubscriptionNode[] => {
  const newTenantList: SubscriptionNode[] = [];

  tenants.sort((a: SubscriptionNode, b: SubscriptionNode) => {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });

  tenants.forEach((tenantNode: SubscriptionNode, tenantIndex: number) => {
    const newCdmList: SubscriptionNode[] = [];

    tenantNode.children.sort((a: SubscriptionNode, b: SubscriptionNode) => {
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });

    tenantNode.children.forEach((cdmNode: SubscriptionNode, cdmIndex: number) => {
      const newAttributeList: SubscriptionNode[] = [];

      cdmNode.children.forEach((attributeNode: SubscriptionNode, attributeIndex: number) => {
        const newAttribute: SubscriptionNode = {
          id: attributeNode.id,
          name: attributeNode.name,
          type: attributeNode.type,
          children: attributeNode.children,
          pathFromRoot: tenantIndex + '###' + cdmIndex + '###' + attributeIndex,
          isCheckedAttribute: false,
          isApprovedAttribute: false,
          isEO14117Protected: attributeNode.isEO14117Protected || false,
          primaryKey: null,
          alternateKeys: null,
          compositeKey: null
        };
        newAttributeList.push(newAttribute);
      });

      const newCdm: SubscriptionNode = {
        id: cdmNode.id,
        name: cdmNode.name,
        type: cdmNode.type,
        children: newAttributeList,
        pathFromRoot: tenantIndex + '###' + cdmIndex,
        isAllSelected: false,
        isEO14117Protected: cdmNode.isEO14117Protected || false,
        primaryKey: cdmNode.primaryKey,
        alternateKeys: cdmNode.alternateKeys,
        compositeKey: cdmNode.compositeKey
      };

      newCdmList.push(newCdm);
    });

    const newTenant: SubscriptionNode = {
      id: tenantNode.id,
      name: tenantNode.name,
      type: SubscriptionNodeTypes.Tenant,
      children: newCdmList,
      pathFromRoot: tenantIndex.toString(),
      isDropdownExpand: false,
      isEO14117Protected: tenantNode.isEO14117Protected || false,
      primaryKey: null,
      alternateKeys: null,
      compositeKey: null
    };

    newTenantList.push(newTenant);
  });
  return newTenantList;
};

export const filterBySearchQuery = (
  formattedSubscriptionNodes: SubscriptionNode[],
  newSearchQuery: string
): SubscriptionNode[] => {

  // Initial Load
  if (!formattedSubscriptionNodes) {
    return [];
  }

  // Clear Search
  if (!newSearchQuery) {
    return formattedSubscriptionNodes;
  }

  // Query Search
  const newTenantList: SubscriptionNode[] = [];
  formattedSubscriptionNodes.forEach((currentTenant: SubscriptionNode) => {
    const newTenant: SubscriptionNode = filterSubscriptionNodeRecusively(currentTenant, newSearchQuery);
    // If there is a match found within the names, add the Tenant to the list
    if (newTenant) {
      newTenantList.push(newTenant);
    }
  });

  // Format CDM level with isAllSelected field
  newTenantList.forEach((tenant: SubscriptionNode) => {
    tenant.children.forEach((cdm: SubscriptionNode) => {
      const numberOfChildren = cdm.children.length;
      let numberOfChecked = 0;
      cdm.children.forEach((attribute: SubscriptionNode) => {
        if (attribute.isCheckedAttribute) {
          numberOfChecked = numberOfChecked + 1;
        }
      });
      if (numberOfChecked === numberOfChildren) {
        cdm.isAllSelected = true;
      } else {
        cdm.isAllSelected = false;
      }
    });
  });

  return newTenantList;
};

const findNodeIndexByName = (targetNodeName: string, nodeList: SubscriptionNode[]) => {
  const foundIndex = nodeList.findIndex((node) => {
    return node.name === targetNodeName;
  });
  return foundIndex;
};

const findNodeIndexById = (targetNodeId: string, nodeList: SubscriptionNode[]) => {
  const foundIndex = nodeList.findIndex((node) => {
    return node.id === targetNodeId;
  });
  return foundIndex;
};

const filterSubscriptionNodeRecusively = (
  node: SubscriptionNode,
  newSearchQuery: string
): SubscriptionNode => {

  if (node.name.toLowerCase().includes(newSearchQuery.toLowerCase())) {
    return node;
  }

  if (!node.children) {
    return null;
  }

  const foundChildNodeList: SubscriptionNode[] = [];
  const currentchildNodeList: SubscriptionNode[] = node.children;
  currentchildNodeList.forEach((childNode) => {
    const foundChild: SubscriptionNode = filterSubscriptionNodeRecusively(childNode, newSearchQuery);
    if (foundChild) {
      foundChildNodeList.push(foundChild);
    }
  });

  if (foundChildNodeList.length == 0) {
    return null;
  }

  const newNode: SubscriptionNode = {
    id: node.id,
    name: node.name,
    type: node.type,
    pathFromRoot: node.pathFromRoot,
    children: foundChildNodeList,
    isDropdownExpand: node.isDropdownExpand,
    isAllSelected: node.isAllSelected,
    isCheckedAttribute: node.isCheckedAttribute,
    isApprovedAttribute: node.isApprovedAttribute,
    primaryKey:null,
    alternateKeys: null,
    compositeKey: null
  };

  return newNode;
};



/*
 * Removes different fields to each level of the SubscriptionNode tree
 * - Tenant Level: isDropdownExpand
 * - Cdm Level: isAllSelected
 * - Attribute Level: isChecked
 */
export const removeSubscriptionNodeFields = (formattedSubscriptionNodes: SubscriptionNode[]): SubscriptionNode[] => {

  const newSubscriptionNodes: SubscriptionNode[] = [];
  formattedSubscriptionNodes.map((tenantNode: SubscriptionNode) => {

    const newCdmList: SubscriptionNode[] = [];
    tenantNode.children.map((cdmNode: SubscriptionNode) => {

      const newAttributeList: SubscriptionNode[] = [];
      cdmNode.children.map((attributeNode: SubscriptionNode) => {

        if (attributeNode.isCheckedAttribute) {
          const newAttributeNode: SubscriptionNode = {
            id: attributeNode.id,
            name: attributeNode.name,
            type: attributeNode.type,
            children: null,
            primaryKey: null,
            alternateKeys: null,
            compositeKey:null
          };

          newAttributeList.push(newAttributeNode);
        }
      });

      if (newAttributeList.length > 0) {
        const newCdmNode: SubscriptionNode = {
          id: cdmNode.id,
          name: cdmNode.name,
          type: cdmNode.type,
          children: newAttributeList,
          primaryKey: cdmNode.primaryKey,
          alternateKeys: cdmNode.alternateKeys,
          compositeKey: cdmNode.compositeKey
        };

        newCdmList.push(newCdmNode);
      }
    });

    if (newCdmList.length > 0) {
      const newTenantNode: SubscriptionNode = {
        id: tenantNode.id,
        name: tenantNode.name,
        type: SubscriptionNodeTypes.Tenant,
        children: newCdmList,
        primaryKey: null,
        alternateKeys: null,
        compositeKey: null
      };

      newSubscriptionNodes.push(newTenantNode);
    }
  });

  return newSubscriptionNodes;
};

/*
 * Like addSubscriptionNodeFields but preserves EgressOption children on approved attributes
 */
export const addSubscriptionNodeFieldsForApproved = (tenants: SubscriptionNode[]): SubscriptionNode[] => {
  const newTenantList: SubscriptionNode[] = [];

  tenants.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

  tenants.forEach((tenantNode, tenantIndex) => {
    const newCdmList: SubscriptionNode[] = [];

    tenantNode.children.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

    tenantNode.children.forEach((cdmNode, cdmIndex) => {
      const newAttributeList: SubscriptionNode[] = [];

      cdmNode.children.forEach((attributeNode, attributeIndex) => {
        const newAttribute: SubscriptionNode = {
          id: attributeNode.id,
          name: attributeNode.name,
          type: attributeNode.type,
          children: attributeNode.children, // preserve EgressOption children
          pathFromRoot: tenantIndex + '###' + cdmIndex + '###' + attributeIndex,
          isCheckedAttribute: false,
          isApprovedAttribute: false,
          isEO14117Protected: attributeNode.isEO14117Protected || false,
          primaryKey: null,
          alternateKeys: null,
          compositeKey: null,
        };
        newAttributeList.push(newAttribute);
      });

      const newCdm: SubscriptionNode = {
        id: cdmNode.id,
        name: cdmNode.name,
        type: cdmNode.type,
        children: newAttributeList,
        pathFromRoot: tenantIndex + '###' + cdmIndex,
        isAllSelected: false,
        isEO14117Protected: cdmNode.isEO14117Protected || false,
        primaryKey: cdmNode.primaryKey,
        alternateKeys: cdmNode.alternateKeys,
        compositeKey: cdmNode.compositeKey,
      };
      newCdmList.push(newCdm);
    });

    const newTenant: SubscriptionNode = {
      id: tenantNode.id,
      name: tenantNode.name,
      type: SubscriptionNodeTypes.Tenant,
      children: newCdmList,
      pathFromRoot: tenantIndex.toString(),
      isDropdownExpand: false,
      isEO14117Protected: tenantNode.isEO14117Protected || false,
      primaryKey: null,
      alternateKeys: null,
      compositeKey: null,
    };
    newTenantList.push(newTenant);
  });
  return newTenantList;
};

/*
 * Mark attributes in availableNodes as approved when they match approvedNodes
 * Uses name-based matching (IDs differ between available/approved APIs)
 * Egress intersection: attribute is locked only when ALL selected egress options are in its approved set
 */
export const markApprovedAttributes = (
  availableNodes: SubscriptionNode[],
  approvedNodes: SubscriptionNode[],
  selectedEgressOptions: number[]
): SubscriptionNode[] => {
  if (!availableNodes || availableNodes.length === 0) return availableNodes;
  if (!approvedNodes || approvedNodes.length === 0) return availableNodes;

  const result: SubscriptionNode[] = JSON.parse(JSON.stringify(availableNodes));

  const selectedEgressNames = selectedEgressOptions
    .map((id) => egressIdToName[id])
    .filter(Boolean);

  result.forEach((tenant) => {
    const approvedTenantIdx = findNodeIndexByName(tenant.name, approvedNodes);
    if (approvedTenantIdx === -1) return;
    const approvedTenant = approvedNodes[approvedTenantIdx];

    tenant.children.forEach((cdm) => {
      const approvedCdmIdx = findNodeIndexByName(cdm.name, approvedTenant.children);
      if (approvedCdmIdx === -1) return;
      const approvedCdm = approvedTenant.children[approvedCdmIdx];

      cdm.children.forEach((attr) => {
        const approvedAttrIdx = findNodeIndexByName(attr.name, approvedCdm.children);
        if (approvedAttrIdx === -1) return;
        const approvedAttr = approvedCdm.children[approvedAttrIdx];

        // Get approved egress names from EgressOption children
        const approvedEgressNames: string[] = (approvedAttr.children || [])
          .filter((c) => c.type === SubscriptionNodeTypes.EgressOption)
          .map((c) => c.name);

        // Check: all selected egress options must be in approved set
        const allSelectedApproved = selectedEgressNames.length > 0 &&
          selectedEgressNames.every((name) => approvedEgressNames.includes(name));

        if (allSelectedApproved) {
          attr.isApprovedAttribute = true;
          attr.isCheckedAttribute = true;
        }
      });
    });
  });

  return result;
};

/*
 * Remove approved attributes before submit (they should not be re-submitted)
 */
export const removeApprovedAttributes = (nodes: SubscriptionNode[]): SubscriptionNode[] => {
  const result: SubscriptionNode[] = JSON.parse(JSON.stringify(nodes));
  result.forEach((tenant) => {
    tenant.children.forEach((cdm) => {
      cdm.children.forEach((attr) => {
        if (attr.isApprovedAttribute) {
          attr.isCheckedAttribute = false;
        }
      });

      const numberOfChildren = cdm.children.length;
      let numberOfChecked = 0;
      cdm.children.forEach((attr) => {
        if (attr.isCheckedAttribute) {
          numberOfChecked = numberOfChecked + 1;
        }
      });
      cdm.isAllSelected = numberOfChecked === numberOfChildren;
    });
  });
  return result;
};

export const addRequiredAttributes = (formattedSubscriptionNodes: SubscriptionNode[]): SubscriptionNode[] => {

  const newSubscriptionNodes: SubscriptionNode[] = JSON.parse(
    JSON.stringify(formattedSubscriptionNodes)
  );

  for (const tenantNode of newSubscriptionNodes) {
    
    for (const cdmNode of tenantNode.children) {

      let singleAttributeChecked = false;

      for (const attributeNode of cdmNode.children) {
        if (attributeNode.isCheckedAttribute) {
          singleAttributeChecked = true;
          break;
        }
      }

      if (singleAttributeChecked) {
        for (const attributeNode of cdmNode.children) {
          if (attributeNode.type === SubscriptionNodeTypes.RequiredAttribute) {
            console.log(SubscriptionNodeTypes.RequiredAttribute);
            attributeNode.isCheckedAttribute = true;
          }
        }
      }
    }
  }

  return newSubscriptionNodes;
};

/*
 * Add key labels to attributes based on CDM-level key metadata
 */
export const addKeyAttributes = (formattedSubscriptionNodes: SubscriptionNode[]): SubscriptionNode[] => {

  const newSubscriptionNodes: SubscriptionNode[] = JSON.parse(
    JSON.stringify(formattedSubscriptionNodes)
  );

  const normalizeKeys = (val: unknown): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(item => typeof item === 'string');
    if (typeof val === 'string') return val ? [val] : [];
    return [];
  };

  for (const tenantNode of newSubscriptionNodes) {
    for (const cdmNode of tenantNode.children) {
      const hasAnyCheckedAttribute = (cdmNode.children || []).some((attributeNode) => attributeNode.isCheckedAttribute);
      if (!hasAnyCheckedAttribute) {
        continue;
      }

      const primaryKeys = new Set(normalizeKeys(cdmNode.primaryKey));
      const alternateKeySet = new Set(normalizeKeys(cdmNode.alternateKeys));
      const compositeKeySet = new Set(normalizeKeys(cdmNode.compositeKey));

      for (const attributeNode of cdmNode.children) {
        if (primaryKeys.has(attributeNode.name) || primaryKeys.has(attributeNode.id)) {
          attributeNode.isCheckedAttribute = true;
          attributeNode.keyLabel = '[PK]';
        } else if (alternateKeySet.has(attributeNode.name) || alternateKeySet.has(attributeNode.id)) {
          attributeNode.isCheckedAttribute = true;
          attributeNode.keyLabel = '[AK]';
        } else if (compositeKeySet.has(attributeNode.name) || compositeKeySet.has(attributeNode.id)) {
          attributeNode.isCheckedAttribute = true;
          attributeNode.keyLabel = '[CK]';
        }
      }
    }
  }

  return newSubscriptionNodes;
};
